import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { App, Alert, Button, Input, Space } from 'antd';
import type { InputRef } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { AuditOutlined } from '@ant-design/icons';
import type { WorkItem, WorkItemContext } from '@/api/types';
import { useAuth } from '@/auth/OlAuthContext';
import { dispositionWorkItem } from '@/api/workItems';
import { isNotAvailable, normalizeApiError } from '@/api/errors';
import {
  dispositionOptions,
  drawerAction,
  escalateOption,
  isTypingTarget,
  optionKeycap,
  type DispositionOption,
} from './keymap';

interface Props {
  item: WorkItem;
  context: WorkItemContext;
  /** Called after a successful disposition so the list + drawer refresh. */
  onDone: (status: string) => void;
  /** Deep-link to the PS-2 approval UX (the flow's PublishDrawer path). */
  onOpenApprovals: (flowId: string, approvalId?: string) => void;
  /** False while another drawer element owns the keyboard (nothing does today). */
  hotkeys?: boolean;
}

/**
 * The per-type disposition surface. Renders the correct actions for the work-item type and posts
 * `/disposition`, degrading honestly on 404 / ALREADY_DONE / USE_APPROVALS_API.
 *
 * Keyboard (OPSFL-POLISH-1): the visible buttons ARE the number keys — `1`–`9` fire the option in
 * the order it is drawn (`keymap.dispositionOptions` is the single source for both), `e` escalates
 * where an escalation outcome exists, `n` jumps to the note. Every one of them stands down while a
 * text field has focus, so a note can never be typed into a decision. The keycap is printed on the
 * button: an operator learns the chord by using the mouse once.
 */
export function DispositionPanel({ item, context, onDone, onOpenApprovals, hotkeys = true }: Props) {
  const { message } = App.useApp();
  const [note, setNote] = useState('');
  const [acting, setActing] = useState<string | null>(null);
  const [freeAction, setFreeAction] = useState('');
  const [linkTarget, setLinkTarget] = useState('');
  const [payloadText, setPayloadText] = useState('');
  const [payloadError, setPayloadError] = useState<string | null>(null);

  const noteRef = useRef<TextAreaRef>(null);
  const linkRef = useRef<InputRef>(null);

  const options = useMemo(() => dispositionOptions(item, context), [item, context]);
  const { user } = useAuth();
  const fourEyes = context.fourEyes ?? null;
  // The server decides; this only saves the maker a refused click.
  const isMaker = !!fourEyes?.makerSub && fourEyes.makerSub === user?.sub;
  const isDissenter = !!fourEyes?.dissenterSub && fourEyes.dissenterSub === user?.sub;
  const escalated = !!fourEyes?.escalated;
  // On a dispute only the two disputed actions are open, and neither reviewer may take them.
  const blocked = (token: string) =>
    isMaker || isDissenter || (escalated && token !== fourEyes?.proposedAction && token !== fourEyes?.dissentAction);

  function parsePayload(): Record<string, unknown> | undefined | null {
    const raw = payloadText.trim();
    if (!raw) return undefined;
    try {
      const v = JSON.parse(raw);
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        setPayloadError(null);
        return v as Record<string, unknown>;
      }
      setPayloadError('Payload must be a JSON object.');
      return null;
    } catch {
      setPayloadError('Payload is not valid JSON.');
      return null;
    }
  }

  const act = useCallback(
    async (action: string, payload?: Record<string, unknown>) => {
      setActing(action);
      try {
        const res = await dispositionWorkItem(item.itemId, action, {
          note: note.trim() || undefined,
          payload,
        });
        if (fourEyes && !fourEyes.proposedAction) {
          message.success(`${action} proposed. A second reviewer must confirm it.`);
        } else if (fourEyes && !escalated && fourEyes.highRisk && action !== fourEyes.proposedAction) {
          message.warning('You disagree on a high-risk task, so a third reviewer will decide.');
        } else {
          message.success(`${action} — item ${res.status.toLowerCase()}`);
        }
        onDone(res.status);
      } catch (e) {
        const n = normalizeApiError(e);
        if (n.code === 'USE_APPROVALS_API') {
          message.info('Approvals are decided in the flow’s maker-checker screen. Opening it.');
          if (context.approvalSummary) {
            onOpenApprovals(context.approvalSummary.flowId, context.approvalSummary.approvalId);
          }
        } else if (n.code === 'FOUR_EYES_SAME_PERSON') {
          message.warning('You proposed this decision, so a second reviewer must make it.');
          onDone(item.status);
        } else if (n.code === 'FOUR_EYES_THIRD_REVIEWER_REQUIRED') {
          message.warning('You have already reviewed this task, so a third reviewer must decide it.');
          onDone(item.status);
        } else if (n.code === 'FOUR_EYES_UNVERIFIABLE') {
          message.error('Could not check who may decide this task. Nothing was recorded. Try again.');
        } else if (n.code === 'ALREADY_DONE') {
          message.warning('This item was already actioned by someone else. Refreshing.');
          onDone('DONE');
        } else if (isNotAvailable(n)) {
          message.info('The disposition endpoint is not live yet (Track A pending).');
        } else {
          message.error(n.message);
        }
      } finally {
        setActing(null);
      }
    },
    [item.itemId, item.status, note, context.approvalSummary, fourEyes, escalated, onDone, onOpenApprovals, message],
  );

  /** One path for every way an option can be chosen — click, number key or `e`. */
  const fire = useCallback(
    (option: DispositionOption) => {
      if (blocked(option.token)) {
        message.warning(
          isMaker || isDissenter ? 'You have already reviewed this task, so someone else must decide it.' : 'This dispute is between the two proposed decisions; choose one of those.',
        );
        return;
      }
      if (option.needsLinkTarget && !linkTarget.trim()) {
        message.warning('Name the entity this signal belongs to before linking it.');
        linkRef.current?.focus();
        return;
      }
      if (item.type === 'HUMAN_TASK') {
        const payload = parsePayload();
        if (payload === null) return; // invalid JSON — blocked, never silently dropped
        void act(option.token, payload);
        return;
      }
      void act(
        option.token,
        option.needsLinkTarget ? { entityId: linkTarget.trim() } : undefined,
      );
    },
    // parsePayload reads local state directly; it is intentionally not a dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [act, item.type, linkTarget, message, isMaker, isDissenter, escalated, fourEyes?.proposedAction, fourEyes?.dissentAction],
  );

  // Number keys / e / n. `[` and `]` belong to the drawer's tab strip and are ignored here.
  useEffect(() => {
    if (!hotkeys) return undefined;
    function onKey(e: KeyboardEvent) {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (isTypingTarget(e.target)) return;
      const action = drawerAction(e.key);
      if (!action) return;
      if (action.kind === 'note') {
        e.preventDefault();
        noteRef.current?.focus();
        return;
      }
      if (action.kind === 'option') {
        const option = options[action.index];
        if (!option) return;
        e.preventDefault();
        fire(option);
        return;
      }
      if (action.kind === 'escalate') {
        const option = escalateOption(options);
        if (!option) return;
        e.preventDefault();
        fire(option);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [options, fire, hotkeys]);

  const noteField = (
    <Input.TextArea
      ref={noteRef}
      placeholder="Note (recorded on the decision — required for escalations by policy)"
      autoSize={{ minRows: 2, maxRows: 4 }}
      value={note}
      onChange={(e) => setNote(e.target.value)}
      // Esc leaves the field instead of closing the whole item: the analyst gets the shortcuts
      // back without losing the drawer (and without losing what they just typed).
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          noteRef.current?.blur();
        }
      }}
      aria-label="Disposition note"
      aria-keyshortcuts="n"
      style={{ marginBottom: 10 }}
    />
  );

  // ---- APPROVAL: never dispose here — deep-link to the maker-checker UX. -----------------------
  if (item.type === 'APPROVAL') {
    const a = context.approvalSummary;
    return (
      <div>
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12, borderRadius: 8 }}
          message="Decided in the flow’s maker-checker screen"
          description="Approvals enforce proposer ≠ approver and the simulation-evidence gate server-side. Open the flow to approve or reject."
        />
        <Button
          type="primary"
          icon={<AuditOutlined />}
          disabled={!a?.flowId}
          onClick={() => a?.flowId && onOpenApprovals(a.flowId, a.approvalId)}
        >
          Open in approvals
        </Button>
      </div>
    );
  }

  const alreadyReviewed = isMaker || isDissenter;
  const disagreement = fourEyes?.highRisk
    ? 'A different choice sends this high-risk task to a third reviewer.'
    : 'A different choice replaces their proposal with yours.';
  const fourEyesBanner = fourEyes && (
    <Alert
      type={fourEyes.proposedAction ? 'warning' : 'info'}
      showIcon
      data-testid="four-eyes-banner"
      style={{ marginBottom: 12, borderRadius: 8 }}
      message={
        escalated
          ? alreadyReviewed
            ? `You have already reviewed this. A third reviewer must decide between ${fourEyes.proposedAction} and ${fourEyes.dissentAction}.`
            : `Two reviewers disagree: ${fourEyes.maker} chose ${fourEyes.proposedAction}, ${fourEyes.dissenter} chose ${fourEyes.dissentAction}`
          : !fourEyes.proposedAction
            ? 'This task needs two reviewers'
            : isMaker
              ? `You proposed ${fourEyes.proposedAction}. A second reviewer must decide it.`
              : `${fourEyes.maker} proposed ${fourEyes.proposedAction}`
      }
      description={
        escalated
          ? [
              fourEyes.makerNote && `${fourEyes.maker}: ${fourEyes.makerNote}.`,
              fourEyes.dissenterNote && `${fourEyes.dissenter}: ${fourEyes.dissenterNote}.`,
              alreadyReviewed ? null : 'Choose one of the two. Your decision is final and the run moves on.',
            ]
              .filter(Boolean)
              .join(' ')
          : !fourEyes.proposedAction
            ? `Your decision is recorded as a proposal. The run moves on once a second person gives the same decision.${fourEyes.highRisk ? ' This subject is high-risk: if a second reviewer disagrees, a third decides.' : ''}`
            : isMaker
              ? fourEyes.makerNote
              : [fourEyes.makerNote, `Choose ${fourEyes.proposedAction} to confirm. ${disagreement}`]
                  .filter(Boolean)
                  .join(' ')
      }
    />
  );

  const optionButtons = (
    <Space wrap>
      {options.map((option, i) => {
        const cap = optionKeycap(i);
        return (
          <Button
            key={option.token}
            type={option.intent === 'positive' ? 'primary' : 'default'}
            danger={option.intent === 'negative'}
            loading={acting === option.token}
            className="ol-keycap-btn"
            aria-keyshortcuts={cap ?? undefined}
            disabled={blocked(option.token)}
            onClick={() => fire(option)}
          >
            {cap && (
              <span className="ol-kbd" aria-hidden>
                {cap}
              </span>
            )}
            {option.label}
          </Button>
        );
      })}
    </Space>
  );

  // ---- SIGNAL_REVIEW: CLEAR / ESCALATE / LINK_ENTITY. -----------------------------------------
  if (item.type === 'SIGNAL_REVIEW') {
    return (
      <div>
        {noteField}
        <Input
          ref={linkRef}
          placeholder="Link target entity id (for LINK ENTITY)"
          value={linkTarget}
          onChange={(e) => setLinkTarget(e.target.value)}
          aria-label="Link target entity id"
          style={{ marginBottom: 10 }}
          className="ol-mono"
        />
        {optionButtons}
      </div>
    );
  }

  // ---- RFI_FOLLOWUP: RESPONSE_RECEIVED / EXPIRE / CANCEL. --------------------------------------
  if (item.type === 'RFI_FOLLOWUP') {
    return (
      <div>
        {noteField}
        {optionButtons}
      </div>
    );
  }

  // ---- HUMAN_TASK: the task's decision options (or a free-form fallback) + optional payload. ---
  return (
    <div>
      {fourEyesBanner}
      {noteField}
      <details style={{ marginBottom: 10 }}>
        <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--ol-text-secondary)' }}>
          Signal payload (optional JSON — the body this option emits)
        </summary>
        <Input.TextArea
          placeholder='e.g. {"riskBand":"HIGH","reasonCode":"ADVERSE_MEDIA"}'
          autoSize={{ minRows: 2, maxRows: 6 }}
          value={payloadText}
          onChange={(e) => {
            setPayloadText(e.target.value);
            setPayloadError(null);
          }}
          className="ol-mono"
          style={{ marginTop: 6 }}
          status={payloadError ? 'error' : undefined}
        />
        {payloadError && (
          <div style={{ color: 'var(--ol-negative)', fontSize: 11, marginTop: 4 }}>{payloadError}</div>
        )}
      </details>

      {options.length > 0 ? (
        optionButtons
      ) : (
        <div>
          <Alert
            type="warning"
            showIcon
            banner
            style={{ marginBottom: 10, borderRadius: 6 }}
            message="No decision options were supplied for this task — enter the option token to record a disposition."
          />
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Decision option token (e.g. APPROVE, REQUEST_INFO)"
              value={freeAction}
              onChange={(e) => setFreeAction(e.target.value)}
              aria-label="Decision option token"
            />
            <Button
              type="primary"
              loading={acting === freeAction && !!freeAction}
              disabled={!freeAction.trim()}
              onClick={() => {
                const payload = parsePayload();
                if (payload === null) return;
                void act(freeAction.trim(), payload);
              }}
            >
              Apply
            </Button>
          </Space.Compact>
        </div>
      )}
    </div>
  );
}
